import { useFeatureIsOn } from "@growthbook/growthbook-react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Button, Dialog } from "@platform/design-system";
import { BTX_CURRENT_CARRIER_POLICY_DROPDOWN } from "@platform/feature-flags";
import { BaseBenefitPlanPolicyPeriod } from "@platform/openapi-types";
import React, { useState } from "react";
import { CurrentCarrierPolicySelect } from "@platform/plan-grid-core";

import { buildPolicyPeriodOptions } from "./buildPolicyPeriodOptions";

export interface AddNewBenefitPlanDialogProps {
  currentPolicyPeriod?: BaseBenefitPlanPolicyPeriod;
  /** Coverage UUID for the Current Carrier - Policy lookup; when absent the
   * dropdown is not shown and the Nickname field is used (ADR 10.22). */
  coverageId?: string;
  /** Plan type (e.g. "ppo") used to narrow the policy lookup. */
  planType?: string;
  onConfirm: (
    nickname: string,
    size: number,
    policyPeriod: BaseBenefitPlanPolicyPeriod,
    currentPolicyUuid?: string,
  ) => void;
  onCancel: VoidFunction;
}

const PLAN_OPTIONS = [
  { label: "Single", value: 1 },
  { label: "Dual", value: 2 },
  { label: "Tri", value: 3 },
] as const;

export const AddNewBenefitPlanDialog: React.FC<
  AddNewBenefitPlanDialogProps
> = ({ currentPolicyPeriod, coverageId, planType, onConfirm, onCancel }) => {
  const policyPeriodOptions = buildPolicyPeriodOptions(currentPolicyPeriod);
  const policyDropdownEnabled = useFeatureIsOn(
    BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
  );

  const [nickname, setNickname] = useState("");
  const [size, setSize] = useState<number>(1);
  const [policyPeriod, setPolicyPeriod] = useState<BaseBenefitPlanPolicyPeriod>(
    policyPeriodOptions[0].value,
  );
  const [currentPolicyUuid, setCurrentPolicyUuid] = useState<
    string | undefined
  >(undefined);

  // For a current plan group with the flag on, the Current Carrier - Policy
  // dropdown replaces the free-text nickname (ADR 10.22).
  const showPolicyDropdown =
    policyDropdownEnabled &&
    policyPeriod === BaseBenefitPlanPolicyPeriod.current &&
    Boolean(coverageId);

  const handleConfirm = (): void => {
    // When the policy dropdown replaces the nickname field, don't send a stale
    // nickname the user may have typed before switching to Current (ADR 10.22).
    onConfirm(
      showPolicyDropdown ? "" : nickname,
      size,
      policyPeriod,
      showPolicyDropdown ? currentPolicyUuid : undefined,
    );
  };

  return (
    <Dialog
      open
      title="Add New Plan Group"
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      actions={
        <>
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="primary">
            Add Plan
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField
          id="policy-period-textfield"
          name="policyPeriod"
          label="Coverage Opportunity"
          select
          fullWidth
          value={policyPeriod}
          onChange={(e) =>
            setPolicyPeriod(e.target.value as BaseBenefitPlanPolicyPeriod)
          }
          slotProps={{
            htmlInput: { id: "policy-period-input", name: "policyPeriod" },
          }}
        >
          {policyPeriodOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id="plan-options-textfield"
          name="planOptions"
          label="Plan Options"
          select
          fullWidth
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          slotProps={{
            htmlInput: { id: "plan-options-input", name: "planOptions" },
          }}
        >
          {PLAN_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {showPolicyDropdown && coverageId ? (
          <CurrentCarrierPolicySelect
            coverageId={coverageId}
            planType={planType}
            value={currentPolicyUuid}
            onChange={setCurrentPolicyUuid}
          />
        ) : (
          <TextField
            id="nickname-textfield"
            name="nickname"
            label="Nickname"
            type="text"
            fullWidth
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            slotProps={{
              htmlInput: { id: "nickname-input", name: "nickname" },
            }}
          />
        )}
      </Box>
    </Dialog>
  );
};

AddNewBenefitPlanDialog.displayName = "AddNewBenefitPlanDialog";
export default AddNewBenefitPlanDialog;
